import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { LeadStatutSelect, STATUTS, statutOf } from "@/components/LeadStatut";
import { RevendeursPanel } from "@/components/admin/RevendeursPanel";
import { FautrasLogo } from "@/components/FautrasLogo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Download, LogOut, UserPlus, Trash2, Users, Inbox, Calculator, KeyRound, Eye, Unlock, RotateCcw } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PROJET = { van: "Van chevaux", fourgon: "Remorque fourgon" };
const ACHAT = { immediat: "Immédiat", moins_3_mois: "< 3 mois", moins_1_an: "< 1 an", plus_1_an: "> 1 an" };

export default function Admin() {
  const { user, loading, logout, authHeader } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("leads");
  const [leads, setLeads] = useState([]);
  const [filtre, setFiltre] = useState("tous");
  const [vendeurs, setVendeurs] = useState([]);
  const [na, setNa] = useState({ name: "", email: "", password: "" });

  const load = useCallback(async () => {
    try {
      const [l, v] = await Promise.all([
        axios.get(`${API}/admin/leads`, { headers: authHeader() }),
        axios.get(`${API}/admin/vendeurs`, { headers: authHeader() }),
      ]);
      setLeads(l.data); setVendeurs(v.data);
    } catch { toast.error("Erreur de chargement."); }
  }, [authHeader]);

  useEffect(() => {
    if (loading) return;
    if (!user || user.role !== "admin") { navigate("/login"); return; }
    load();
  }, [user, loading, navigate, load]);

  const exportLeads = async () => {
    try {
      const res = await axios.get(`${API}/admin/leads/export`, { headers: authHeader(), responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "Fautras_leads.xlsx"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Export impossible."); }
  };

  const addAdmin = async (e) => {
    e.preventDefault();
    try {
      const { data } = await axios.post(`${API}/admin/admins`, na, { headers: authHeader() });
      toast.success(data.status === "promoted" ? `${data.email} est maintenant administrateur.` : "Administrateur créé.");
      setNa({ name: "", email: "", password: "" });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Création impossible.");
    }
  };

  const delVendeur = async (id) => {
    if (!window.confirm("Supprimer ce revendeur ?\n\nSon compte sera supprimé et ses départements ne lui seront plus attribués (les leads iront au contact par défaut). Ses leads déjà reçus sont conservés.")) return;
    try { await axios.delete(`${API}/admin/vendeurs/${id}`, { headers: authHeader() }); load(); }
    catch { toast.error("Suppression impossible."); }
  };

  const resetVendeur = async (v) => {
    if (!window.confirm(`Réinitialiser le mot de passe de ${v.name} ?\n\nIl retrouvera le mot de passe provisoire commun, son compte sera débloqué et il devra choisir un nouveau mot de passe à sa prochaine connexion.`)) return;
    try {
      await axios.post(`${API}/admin/vendeurs/${v.id}/reset-password`, {}, { headers: authHeader() });
      toast.success(`Mot de passe de ${v.name} réinitialisé au mot de passe provisoire.`);
      load();
    } catch { toast.error("Réinitialisation impossible."); }
  };

  const unlockVendeur = async (v) => {
    try {
      await axios.post(`${API}/admin/vendeurs/${v.id}/unlock`, {}, { headers: authHeader() });
      toast.success(`Compte de ${v.name} débloqué.`);
      load();
    } catch { toast.error("Déblocage impossible."); }
  };

  const delLead = async (id) => {
    if (!window.confirm("Supprimer définitivement ce lead ?")) return;
    try {
      await axios.delete(`${API}/admin/leads/${id}`, { headers: authHeader() });
      setLeads((prev) => prev.filter((l) => l.id !== id));
      toast.success("Lead supprimé.");
    } catch { toast.error("Suppression impossible."); }
  };

  const openSimulation = (lead) => {
    const answers = lead?.simulation?.answers;
    if (!answers) { toast.error("Simulation non disponible pour ce client."); return; }
    sessionStorage.setItem("fautras_prefill", JSON.stringify(answers));
    navigate("/");
  };

  const [sending, setSending] = useState(false);
  const sendCredentials = async () => {
    if (!window.confirm(`Envoyer par email l'accès (lien + identifiants) à ${vendeurs.length} vendeur(s) ?\n\nLe lien pointera vers : ${window.location.origin}`)) return;
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/admin/vendeurs/send-credentials`,
        { site_url: window.location.origin }, { headers: authHeader() });
      toast.success(`Emails envoyés : ${data.sent}${data.failed ? ` · échecs : ${data.failed}` : ""}`);
    } catch { toast.error("Envoi impossible."); }
    setSending(false);
  };

  if (loading || !user) return <div className="min-h-screen bg-[#faf7f1]" />;

  return (
    <div className="min-h-screen bg-[#faf7f1]">
      <header className="bg-[#434343] text-white">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <FautrasLogo dark />
          <div className="flex items-center gap-3">
            <span className="text-sm text-white/70 hidden sm:inline">{user.email}</span>
            <Button size="sm" variant="secondary" onClick={() => navigate("/")}
              data-testid="admin-simulateur" className="rounded-full">
              <Calculator className="h-4 w-4 mr-1.5" /> Simulateur
            </Button>
            <Button size="sm" variant="secondary" onClick={() => navigate("/compte")}
              data-testid="admin-compte" className="rounded-full">
              <KeyRound className="h-4 w-4 mr-1.5" /> Mon compte
            </Button>
            <Button size="sm" variant="secondary" onClick={() => { logout(); navigate("/login"); }}
              data-testid="admin-logout" className="rounded-full">
              <LogOut className="h-4 w-4 mr-1.5" /> Déconnexion
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex gap-2 mb-6">
          <TabBtn active={tab === "leads"} onClick={() => setTab("leads")} icon={Inbox} label={`Leads (${leads.length})`} testId="admin-tab-leads" />
          <TabBtn active={tab === "vendeurs"} onClick={() => setTab("vendeurs")} icon={Users} label={`Revendeurs (${vendeurs.length})`} testId="admin-tab-vendeurs" />
        </div>

        {tab === "leads" && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <h1 className="font-heading text-2xl font-extrabold text-[#2b2b2b]">Leads reçus</h1>
              <div className="flex items-center gap-3">
              <select value={filtre} onChange={(e) => setFiltre(e.target.value)} data-testid="admin-statut-filter"
                aria-label="Filtrer par statut"
                className="h-10 rounded-xl border border-border bg-white px-3 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-[#9E7B41]/30">
                <option value="tous">Tous les statuts ({leads.length})</option>
                {STATUTS.map((s) => (
                  <option key={s.v} value={s.v}>{s.t} ({leads.filter((l) => statutOf(l) === s.v).length})</option>
                ))}
              </select>
              <Button onClick={exportLeads} data-testid="admin-export"
                className="bg-[#9E7B41] hover:bg-[#876634] rounded-xl">
                <Download className="h-4 w-4 mr-2" /> Exporter Excel
              </Button>
              </div>
            </div>
            <div className="bg-white rounded-2xl border border-border overflow-x-auto">
              <table className="w-full text-sm" data-testid="admin-leads-table">
                <thead className="bg-[#faf7f1] text-left text-xs uppercase tracking-wider text-muted-foreground">
                  <tr>
                    <th className="px-4 py-3">Date</th><th className="px-4 py-3">Statut</th><th className="px-4 py-3">Client</th>
                    <th className="px-4 py-3">Contact</th><th className="px-4 py-3">CP / Ville</th>
                    <th className="px-4 py-3">Projet</th><th className="px-4 py-3">Échéance</th>
                    <th className="px-4 py-3">Vendeur</th>
                    <th className="px-4 py-3 text-right">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {leads.filter((l) => filtre === "tous" || statutOf(l) === filtre).map((l) => (
                    <tr key={l.id} className="border-t border-border">
                      <td className="px-4 py-3 text-muted-foreground whitespace-nowrap">{(l.created_at || "").slice(0, 10)}</td>
                      <td className="px-4 py-3">
                        <LeadStatutSelect lead={l} authHeader={authHeader}
                          onChanged={(id, statut) => setLeads((p) => p.map((x) => (x.id === id ? { ...x, statut } : x)))} />
                      </td>
                      <td className="px-4 py-3 font-semibold text-[#2b2b2b]">
                        {l.prenom} {l.nom}
                        {l.societe && <div className="text-xs font-normal text-muted-foreground" data-testid={`admin-lead-societe-${l.id}`}>{l.societe}</div>}
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">{l.email}<br />{l.tel}</td>
                      <td className="px-4 py-3">{l.code_postal} {l.ville}</td>
                      <td className="px-4 py-3">
                        {PROJET[l.projet] || l.projet}
                        {l.montant_reprise > 0 && (
                          <div className="text-xs text-muted-foreground" data-testid={`admin-lead-reprise-${l.id}`}>
                            Reprise : {Number(l.montant_reprise).toLocaleString("fr-FR")} €
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3">{ACHAT[l.projet_achat] || l.projet_achat}</td>
                      <td className="px-4 py-3 text-[#9E7B41] font-medium">{l.vendeur?.vendeur}</td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button size="sm" variant="outline" onClick={() => openSimulation(l)}
                            data-testid={`admin-lead-open-${l.id}`} className="rounded-full whitespace-nowrap">
                            <Eye className="h-4 w-4 mr-1.5" /> Voir la simulation
                          </Button>
                          <button onClick={() => delLead(l.id)} data-testid={`admin-lead-delete-${l.id}`}
                            aria-label="Supprimer le lead"
                            className="text-muted-foreground hover:text-red-600 transition-colors p-1.5 rounded-lg hover:bg-red-50">
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {leads.length === 0 && (
                    <tr><td colSpan={9} className="px-4 py-10 text-center text-muted-foreground">Aucun lead pour le moment.</td></tr>
                  )}
                  {leads.length > 0 && filtre !== "tous" && !leads.some((l) => statutOf(l) === filtre) && (
                    <tr><td colSpan={9} className="px-4 py-10 text-center text-muted-foreground" data-testid="admin-statut-empty">Aucun lead pour ce statut.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === "vendeurs" && (
          <>
          <div className="mb-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 bg-white rounded-2xl border border-border p-4">
            <p className="text-sm text-muted-foreground">
              Envoie à chaque vendeur un email avec l'explication du service, le lien de connexion et ses identifiants.
            </p>
            <Button onClick={sendCredentials} disabled={sending || vendeurs.length === 0}
              data-testid="admin-send-credentials" className="rounded-full bg-[#9E7B41] hover:bg-[#876634] shrink-0">
              <Inbox className="h-4 w-4 mr-1.5" /> {sending ? "Envoi en cours…" : "Envoyer les identifiants à tous"}
            </Button>
          </div>
          <RevendeursPanel authHeader={authHeader} onReset={resetVendeur} onUnlock={unlockVendeur} onDelete={delVendeur} onChanged={load} />
          <div className="mt-6">
            <form onSubmit={addAdmin} data-testid="admin-add-admin"
              className="bg-white rounded-2xl border border-border p-6 space-y-4 max-w-md">
              <h2 className="font-heading text-lg font-extrabold text-[#2b2b2b] flex items-center gap-2">
                <KeyRound className="h-5 w-5 text-[#9E7B41]" /> Ajouter un admin
              </h2>
              <p className="text-xs text-muted-foreground">Accès complet : tous les leads, les vendeurs et les exports. Un compte existant est promu administrateur.</p>
              <div><Label className="text-sm font-semibold">Nom</Label>
                <Input value={na.name} data-testid="admin-new-name" onChange={(e) => setNa({ ...na, name: e.target.value })} className="mt-2 h-11" required /></div>
              <div><Label className="text-sm font-semibold">Email</Label>
                <Input type="email" value={na.email} data-testid="admin-new-email" onChange={(e) => setNa({ ...na, email: e.target.value })} className="mt-2 h-11" required /></div>
              <div><Label className="text-sm font-semibold">Mot de passe (8 caractères min.)</Label>
                <Input value={na.password} data-testid="admin-new-password" onChange={(e) => setNa({ ...na, password: e.target.value })} className="mt-2 h-11" type="password" autoComplete="new-password" minLength={8} required /></div>
              <Button type="submit" data-testid="admin-new-submit" className="w-full h-11 bg-[#2b2b2b] hover:bg-[#434343] rounded-xl">Créer l'administrateur</Button>
            </form>
          </div>
          </>
        )}
      </div>
    </div>
  );
}

const TabBtn = ({ active, onClick, icon: Icon, label, testId }) => (
  <button onClick={onClick} data-testid={testId}
    className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold transition-colors ${
      active ? "bg-[#434343] text-white" : "bg-white text-muted-foreground border border-border hover:text-[#9E7B41]"
    }`}>
    <Icon className="h-4 w-4" /> {label}
  </button>
);
